import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, HostListener, Input, ViewChild, ViewChildren, QueryList, AfterViewInit, DestroyRef, inject, HostBinding } from '@angular/core';
import { BehaviorSubject, merge, fromEvent, map, throttleTime } from 'rxjs';
import { SeekSliderComponent } from './seek-slider/seek-slider.component';
import { VideoIntl } from './video.intl';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProgressIndicatorComponent } from '@knorr-bremse-portals/ngx-components/components/progress-indicator';
import { IconComponent } from '@knorr-bremse-portals/ngx-components/components/icon';
import { DurationPipe } from '@knorr-bremse-portals/ngx-components/pipes/duration';
import { FormatDurationPipe } from '@knorr-bremse-portals/ngx-components/pipes/format-duration';
import { iconArrowRight, iconEndFullscreen, iconFullscreen, iconPause, iconSoundOff, iconSoundOn } from '@knorr-bremse-portals/icons';

export type CropType = 'top' | 'bottom' | 'both' | 'fit';

@Component({
  selector: 'kb-video',
  templateUrl: './video.component.html',
  styleUrls: ['./video.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ CommonModule, ProgressIndicatorComponent, SeekSliderComponent, IconComponent, DurationPipe, FormatDurationPipe ],
})
export class VideoComponent implements OnInit, AfterViewInit {

  private static readonly hideControlsTime = 1500; // TODO: move the time to a central place

  @Input({ required: true }) public src!: string;
  @Input({ required: false }) public poster?: string;
  @Input() public crop: CropType = 'fit';
  @Input() public autoplay = false;
  @Input() public loop = false;
  @HostBinding('attr.controls') @Input() public controls: 'full' | 'minimal' = 'full';

  @ViewChild('videoElement', { static: true }) private videoElement!: ElementRef<HTMLMediaElement>;
  @ViewChild('videoSourceElement', { static: true }) private videoSourceElement!: ElementRef<HTMLSourceElement>;
  @ViewChildren('seekSlider') private seekSliderElements!: QueryList<SeekSliderComponent>;

  protected iconPlay = iconArrowRight;
  protected iconPause = iconPause;
  protected iconFullscreen = iconFullscreen;
  protected iconEndFullscreen = iconEndFullscreen;
  protected iconSoundOff = iconSoundOff;
  protected iconSoundOn = iconSoundOn;

  protected controlsVisible = false;

  protected secondsMax?: number;

  protected secondsNow = 0;

  private playingSubject$ = new BehaviorSubject<boolean>(false);
  protected readonly playing$ = this.playingSubject$.asObservable();

  private fullscreenSubject$ = new BehaviorSubject<boolean>(false);
  protected readonly fullscreen$ = this.fullscreenSubject$.asObservable();

  private volumeSubject$ = new BehaviorSubject<number>(1);
  protected readonly volume$ = this.volumeSubject$.asObservable();

  private mutedSubject$ = new BehaviorSubject<boolean>(false);
  protected readonly muted$ = this.mutedSubject$.asObservable();

  private loadingSubject$ = new BehaviorSubject<boolean>(false);
  protected readonly loading$ = this.loadingSubject$.asObservable();

  private destroyRef = inject(DestroyRef);
  private hostElement = inject(ElementRef);
  private cdr = inject(ChangeDetectorRef);
  protected intl = inject(VideoIntl);

  public constructor() {
    this.intl.changes$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.cdr.markForCheck());
  }

  public ngOnInit(): void {
    this.initPlayingListener();
    this.initVolumeAndMutedListener();
    this.initLoadingListener();
    this.initErrorListener();
    this.initTimeUpdateListener();
    this.initDurationChangeListener();
  }

  public ngAfterViewInit(): void {
    this.initSeekingListener();
  }

  private initSeekingListener(): void {
    this.seekSliderElements.changes.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((seekSliderElements: QueryList<SeekSliderComponent>) => {
      seekSliderElements.first.secondsNowChange.pipe(
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(secondsNow => {
        this.videoElement.nativeElement.currentTime = secondsNow;
      })
    });
  }

  private initTimeUpdateListener(): void {
    fromEvent<Event>(this.videoElement.nativeElement, 'timeupdate').pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.secondsNow = this.videoElement.nativeElement.currentTime;
      this.cdr.markForCheck();
    });
  }

  private initDurationChangeListener(): void {
    fromEvent<Event>(this.videoElement.nativeElement, 'durationchange').pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.secondsMax = this.videoElement.nativeElement.duration;
      this.cdr.markForCheck();
    });
    // in case the above event handler is attached after the autoplay started playing this corrects the state
    if (!isNaN(this.videoElement.nativeElement.duration)) {
      this.secondsMax = this.videoElement.nativeElement.duration;
      this.cdr.markForCheck();
    }
  }

  private initErrorListener() {
    fromEvent<Event>(this.videoSourceElement.nativeElement, 'error')
    .pipe(
      throttleTime(3000, undefined, { leading: true, trailing: true }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.videoElement.nativeElement.load();
      this.videoElement.nativeElement.play();
    })
  }

  private isVideoPlaying(): boolean {
    const videoElement = this.videoElement.nativeElement;
    return videoElement.currentTime > 0 && !videoElement.paused && !videoElement.ended && videoElement.readyState > 2;
  }

  private initPlayingListener(): void {
    // This automatically fires play, if autoplay is set, otherwise does not fire automatically
    merge(
      fromEvent<Event>(this.videoElement.nativeElement, 'play').pipe(map(() => true)),
      fromEvent<Event>(this.videoElement.nativeElement, 'pause').pipe(map(() => false)),
      fromEvent<Event>(this.videoElement.nativeElement, 'emptied').pipe(map(() => false)),
    ).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(state => {
      this.playingSubject$.next(state)
    });
    // in case the above event handler is attached after the autoplay started playing this corrects the state
    if (this.isVideoPlaying()) {
      this.playingSubject$.next(true);
    }
  }

  private initVolumeAndMutedListener(): void {
    // Also triggers on muted
    // Automatically triggers on autoplay
    fromEvent<Event>(this.videoElement.nativeElement, 'volumechange')
    .pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      // Volume is not changed on muted, these are separate values
      this.volumeSubject$.next(this.videoElement.nativeElement.volume);
      this.mutedSubject$.next(this.videoElement.nativeElement.muted);
    });
  }

  private initLoadingListener(): void {
    merge(
      fromEvent<Event>(this.videoElement.nativeElement, 'stalled').pipe(map(() => true)),
      fromEvent<Event>(this.videoElement.nativeElement, 'waiting').pipe(map(() => true)),
      fromEvent<Event>(this.videoElement.nativeElement, 'canplay').pipe(map(() => false)),
      fromEvent<Event>(this.videoElement.nativeElement, 'canplaythrough').pipe(map(() => false)),
    ).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(state => {
      this.loadingSubject$.next(state)
    });
  }

  public async togglePlaying(): Promise<void> {
    if (this.playingSubject$.value) {
      this.videoElement.nativeElement.pause();
    } else {
      await this.videoElement.nativeElement.play();
    }
  }

  public async toggleFullscreen(): Promise<void> {
    if (document.fullscreenElement !== this.hostElement.nativeElement) {
      try {
        await this.hostElement.nativeElement.requestFullscreen();
        this.fullscreenSubject$.next(true);
      } catch(error) {
        console.warn('Could not enter fullscreen', error);
      }
    } else {
      try {
        await document.exitFullscreen();
        this.fullscreenSubject$.next(false);
      } catch(error) {
        console.warn('Could not exit fullscreen', error);
      }
    }
  }

  public toggleMuted(): void {
    this.videoElement.nativeElement.muted = !this.videoElement.nativeElement.muted;
  }

  private hideControlsTimeout?: number;
  @HostListener('mousemove')
  private showControls() {
    this.controlsVisible = true;
    clearTimeout(this.hideControlsTimeout);
    this.hideControlsTimeout = window.setTimeout(() => {
      this.controlsVisible = false;
      this.cdr.markForCheck();
    }, VideoComponent.hideControlsTime);
  }
  @HostListener('mouseleave')
  private hideControls() {
    clearTimeout(this.hideControlsTimeout);
    this.controlsVisible = false;
  }

}
